import { SettingOutlined } from '@ant-design/icons'
import { Button, Tabs, Tooltip } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useAuthSession } from '@/lib/session'
import FeatureShell from '@/components/FeatureShell'
import {
  DataSource,
  StaffAssignedDataPoint,
  type CapacitySettings,
} from '../types'
import { AllocationTab, CapacityTab, WorkflowTab } from './components'

interface Props {
  settings: CapacitySettings
}

function CapacityIcon() {
  return (
    <svg className="tab-icon h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 20V10M18 20V4M6 20v-4" />
    </svg>
  )
}

function WorkflowIcon() {
  return (
    <svg className="tab-icon h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  )
}

function AllocationIcon() {
  return (
    <svg className="tab-icon h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  )
}

function TabLabel({ icon, label }: { icon: JSX.Element; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      {icon}
      <span>{label}</span>
    </span>
  )
}

const DATA_SOURCE_LABELS: Record<DataSource, string> = {
  [DataSource.XpmJob]: 'XPM Job',
  [DataSource.XpmJobTask]: 'XPM Job-Task',
  [DataSource.FYI]: 'FYI',
  [DataSource.Karbon]: 'Karbon',
}

const STAFF_ASSIGNED_LABELS: Record<StaffAssignedDataPoint, string> = {
  [StaffAssignedDataPoint.XpmJobManager]: 'XPM Job Manager',
  [StaffAssignedDataPoint.XpmJobStaff]: 'XPM Job Staff',
  [StaffAssignedDataPoint.DashboardInsightsOnly]: 'Dashboard Insights only',
  [StaffAssignedDataPoint.XpmJobTaskStaff]: 'XPM Job Task Staff',
  [StaffAssignedDataPoint.FyiJobManager]: 'FYI Job Manager',
}

function formatBudgetMode(settings: CapacitySettings) {
  return settings.budgetMode === 'dollars' ? 'Dollars' : 'Hours'
}

function formatGranularity(settings: CapacitySettings) {
  return settings.defaultGranularity === 'monthly' ? 'Monthly' : 'Weekly'
}

function SettingsSummary({ settings }: { settings: CapacitySettings }) {
  const items = [
    { label: 'Source', value: DATA_SOURCE_LABELS[settings.dataSource] ?? 'Unknown' },
    { label: 'Staff Assignment Source', value: STAFF_ASSIGNED_LABELS[settings.staffAssignedDataPoint] ?? 'Unknown' },
    { label: 'Budget', value: formatBudgetMode(settings) },
    { label: 'Granularity', value: formatGranularity(settings) },
  ]

  return (
    <dl className="flex max-w-full flex-wrap justify-start gap-2 md:justify-end">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm"
        >
          <dt className="text-[11px] font-semibold uppercase leading-none text-gray-400">
            {item.label}
          </dt>
          <dd className="mt-1 text-xs font-semibold leading-tight text-gray-800">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function DashboardPage({ settings }: Props) {
  const navigate = useNavigate()
  const session = useAuthSession()
  const clientDisplayName = session?.currentClientName?.trim() || session?.currentClientId || ''

  return (
    <FeatureShell
      featureKey="pracPlanner"
      withAntd
      bodyClassName="p-6"
      header={
        <>
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-bold text-gray-900 tracking-tight">
                PracPlanner
              </h1>
              <p className="text-sm text-gray-500 mt-1">Client: {clientDisplayName}</p>
            </div>
            <div className="flex items-start gap-2 md:max-w-[680px]">
              <SettingsSummary settings={settings} />
              <Tooltip title="PracPlanner settings">
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  aria-label="Open PracPlanner settings"
                  onClick={() => navigate('/capacity-planner/settings')}
                />
              </Tooltip>
            </div>
          </div>
          <div className="feature-shell__accent mt-3 h-1.5 w-16 rounded-full" aria-hidden />
        </>
      }
    >
      <Tabs
        items={[
          { key: 'capacity', label: <TabLabel icon={<CapacityIcon />} label="Capacity" />, children: <CapacityTab settings={settings} /> },
          { key: 'workflow', label: <TabLabel icon={<WorkflowIcon />} label="Workflow" />, children: <WorkflowTab settings={settings} /> },
          { key: 'allocation', label: <TabLabel icon={<AllocationIcon />} label="Allocation" />, children: <AllocationTab settings={settings} /> },
        ]}
      />
    </FeatureShell>
  )
}
